import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { RoutePath, Path } from 'src/app/shared/route';
import { PlanService } from 'src/app/services/plan/plan.service';
import { filter } from 'rxjs';
import { PlanInterval, PlanUsage } from 'src/app/types/plan';
import dayjs from 'dayjs';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { chartUsageDisplay } from 'src/app/shared/chart-usage-display';
import { planDisplayName } from 'src/app/shared/plan-display-name';

@Component({
  selector: 'app-plan-usage-indicator',
  templateUrl: './plan-usage-indicator.component.html',
  standalone: true,
  imports: [RouterLink, TranslatePipe, NgClass, NgTemplateOutlet],
  styles: [
    `
      .normal {
        @apply bg-primary;
      }

      .warning {
        @apply bg-yellow-500;
      }

      .error {
        @apply bg-red-500;
      }
    `,
  ],
})
export class PlanUsageIndicatorComponent extends BaseComponent implements OnInit {
  private readonly planService = inject(PlanService);

  readonly planAndUsagePath = RoutePath.getPath(Path['setting/billing/plan-and-usage']).fullPath;

  readonly planUsage = signal<PlanUsage | undefined>(undefined);

  readonly name = computed(() => planDisplayName(this.planUsage()?.activation?.planSnapshot));
  readonly isMonthly = computed(
    () => this.planUsage()?.activation?.planSnapshot?.interval === PlanInterval.Every30Days,
  );
  readonly isAnnual = computed(() => this.planUsage()?.activation?.planSnapshot?.interval === PlanInterval.Annual);
  readonly isUsingFreePlan = computed(() => this.planUsage()?.isUsingFreePlan ?? true);
  readonly isInFreeTrial = computed(() => this.planUsage()?.isStillInTrialPeriod ?? false);
  readonly startDate = computed(() => dayjs(this.planUsage()?.activation?.periodStart).format('MMM DD, YYYY'));
  readonly nextChargeDate = computed(() => {
    let ncd = dayjs(this.planUsage()?.activation?.periodStart);

    if (this.isInFreeTrial()) ncd = ncd.add(30, 'day');

    if (this.isMonthly()) ncd = ncd.add(1, 'month');

    if (this.isAnnual()) ncd = ncd.add(1, 'year');

    return ncd.subtract(2, 'day').format('MMM DD, YYYY');
  });
  readonly aiSizeRecommendationUsage = computed(() => this.planUsage()?.usages.aiSizeRecommendation ?? 0);
  readonly aiSizeRecommendationLimit = computed(
    () => this.planUsage()?.activation.planSnapshot.aiSizeRecommendationLimit ?? 0,
  );
  readonly stockNotificationUsage = computed(() => this.planUsage()?.usages.stockNotification ?? 0);
  readonly stockNotificationLimit = computed(
    () => this.planUsage()?.activation.planSnapshot.stockNotificationLimit ?? 0,
  );
  readonly aiSizeRecommendationPerc = computed(
    () => (this.aiSizeRecommendationUsage() / Math.max(1, this.aiSizeRecommendationLimit())) * 100,
  );
  readonly stockNotificationPerc = computed(
    () => (this.stockNotificationUsage() / Math.max(1, this.stockNotificationLimit())) * 100,
  );
  readonly aiSizeRecommendationState = computed(() => {
    if (this.aiSizeRecommendationPerc() < 50) return 'normal';
    if (this.aiSizeRecommendationPerc() < 80) return 'warning';
    return 'error';
  });
  readonly stockNotificationState = computed(() => {
    if (this.stockNotificationPerc() < 50) return 'normal';
    if (this.stockNotificationPerc() < 80) return 'warning';
    return 'error';
  });

  // Published charts against the plan's limits; undefined (rows hidden) from a backend without chartUsage.
  readonly sizeCharts = computed(() => chartUsageDisplay(this.planUsage()?.chartUsage?.size));
  readonly tableCharts = computed(() => chartUsageDisplay(this.planUsage()?.chartUsage?.table));

  ngOnInit() {
    this.sub$ = this.planService.plan$.pipe(filter((plan) => !!plan)).subscribe((plan) => {
      this.planUsage.set(plan);
    });
  }
}
